import {gql} from '@apollo/client';
import {useMemo} from 'react';

import {assertAndContinue} from 'Asserts';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {
  CHIP_ITEM_TYPE_TO_PREDICATE,
  ChipItemType,
  runQueryOutputToChipItemIds,
} from 'insights/analytics-visualizations/chip-items/ChipItems';
import {RunQueryOutputLegacyData} from 'insights/warehouse/AnalyticsWarehouseTypes';

import {
  AnalyticsChipItemResolutionQuery,
  AnalyticsChipItemResolutionQueryVariables,
} from './__generated/AnalyticsChipItemResolutionQuery';
import {ResolvedChipItemIdMap} from './ResolvedChipItemTypes';

export interface UseResolvedChipItemIdMapOutput {
  queryResultType: QueryResultType;
  resolvedChipItemIdMap: ResolvedChipItemIdMap;
}

// Keep items fetched in sync with BulkChipItemPrefetchQuery
export const ANALYTICS_CHIP_ITEM_RESOLUTION_QUERY = gql`
  query AnalyticsChipItemResolutionQuery(
    $bioentityIds: [StrictString!]!
    $entryIds: [StrictString!]!
    $requestIds: [StrictString!]!
    $processIds: [StrictString!]!
    $processOutputIds: [StrictString!]!
    $processTaskIds: [StrictString!]!
    $storableIds: [StrictString!]!
    $studyIds: [StrictString!]!
    $worksheetIds: [StrictString!]!
  ) @skipAudit @engComponent(name: INSIGHTS) {
    bioentities(ids: $bioentityIds) {
      ... on RegistryItemInterface {
        id
        displayLabel
      }
      ...InaccessibleInfoGraphQL
    }
    entries(ids: $entryIds) {
      ... on EntryNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    processes(ids: $processIds) {
      ... on ProcessNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    processOutputs(ids: $processOutputIds) {
      ... on ProcessOutputNode {
        id
        name
        process {
          id
        }
      }
      ...InaccessibleInfoGraphQL
    }
    processTasks(ids: $processTaskIds) {
      ... on ProcessTaskNode {
        id
        name
        taskSchema {
          ... on ProcessTaskSchemaNode {
            id
            clientOrigin
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
    requests(ids: $requestIds) {
      ... on RequestNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    storables(ids: $storableIds) {
      ... on StorableInterface {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    studies(ids: $studyIds) {
      ... on StudyNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
    worksheets(ids: $worksheetIds) {
      ... on WorksheetNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function countItemIdsInQueryVariables(queryVariables: AnalyticsChipItemResolutionQueryVariables): number {
  return Object.values(queryVariables).flat().length;
}

function itemIdsToQueryVariables(itemIds: Array<string>): AnalyticsChipItemResolutionQueryVariables {
  const queryVariables: AnalyticsChipItemResolutionQueryVariables = {
    bioentityIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.BIOENTITY](id)),
    entryIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.ENTRY](id)),
    processIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.PROCESS](id)),
    processOutputIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.PROCESS_OUTPUT](id)),
    processTaskIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.PROCESS_TASK](id)),
    requestIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.REQUEST](id)),
    storableIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.STORABLE](id)),
    studyIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.STUDY](id)),
    worksheetIds: itemIds.filter((id) => CHIP_ITEM_TYPE_TO_PREDICATE[ChipItemType.WORKSHEET](id)),
  };
  assertAndContinue(
    itemIds.length === countItemIdsInQueryVariables(queryVariables),
    'Expected to query for all itemIds, but some are missing'
  );
  return queryVariables;
}

function queryDataToResolvedChipItemIdMap(
  queryData: AnalyticsChipItemResolutionQuery
): ResolvedChipItemIdMap {
  const map: ResolvedChipItemIdMap = {};
  for (const item of [
    ...accessibleObjs(queryData.entries),
    ...accessibleObjs(queryData.requests),
    ...accessibleObjs(queryData.processes),
    ...accessibleObjs(queryData.processOutputs),
    ...accessibleObjs(queryData.processTasks),
    ...accessibleObjs(queryData.storables),
    ...accessibleObjs(queryData.studies),
    ...accessibleObjs(queryData.worksheets),
  ]) {
    map[item.id] = {name: item.name};
  }
  for (const bioentity of accessibleObjs(queryData.bioentities)) {
    map[bioentity.id] = {name: bioentity.displayLabel};
  }
  return map;
}

/**
 * Load chip items by ids
 *
 * The query results (runQueryOutput) contain item ids that we want to resolve and load their properties. For
 * example, runQueryOutputToTSV needs item name.
 *
 * Return resolvedChipItemIdMap (map from item ids to ResolvedChipItems) and queryResultType (status of the
 * request that loaded them).
 *
 * Keep in sync with benchling/insights/analytics_visualizations/chip_items/resolve_chip_item_api_id_map.py
 */
export default function useResolvedChipItemIdMap(
  runQueryOutput: RunQueryOutputLegacyData,
  skipResolution: boolean = false
): UseResolvedChipItemIdMapOutput {
  const queryVariables = useMemo(() => {
    const itemIds = Array.from(runQueryOutputToChipItemIds(runQueryOutput));
    return itemIdsToQueryVariables(itemIds);
  }, [runQueryOutput]);

  const result = useSkippableBenchlingQuery<
    AnalyticsChipItemResolutionQuery,
    AnalyticsChipItemResolutionQueryVariables
  >(ANALYTICS_CHIP_ITEM_RESOLUTION_QUERY, {variables: queryVariables, skip: skipResolution});

  const queryData = result.type === QueryResultType.SUCCESS ? result.data : null;
  const resolvedChipItemIdMap = useMemo(
    () => (queryData != null ? queryDataToResolvedChipItemIdMap(queryData) : {}),
    [queryData]
  );
  return {queryResultType: result.type, resolvedChipItemIdMap};
}
